import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [f, setF] = useState({ name: "", email: "", phone: "", area: "Design", cover: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/jobs").then(r => setJobs(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/jobs/apply", f); toast.success("Application received. Welcome to the talent community."); setF({ name: "", email: "", phone: "", area: "Design", cover: "" }); }
    catch { toast.error("Please try again."); }
    setBusy(false);
  };

  return (
    <div className="bg-ivory text-ink pt-32 pb-24 min-h-screen">
      <PageMeta title="Careers · Join the LOOXS Atelier" description="Live positions and how to apply to the LOOXS atelier. Write to careers@looxsfashion.com or join our talent community — designers, tailors, concierge, retail leadership." />
      <div className="max-w-[1200px] mx-auto px-8 md:px-16">
        <div className="text-center mb-16">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">Careers at LOOXS</p>
          <h1 className="font-editorial text-5xl md:text-8xl leading-[0.95] tracking-tight">Build the house with us.</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ink/70 leading-relaxed">LOOXS is looking for people who care deeply about fashion, design, craftsmanship and long-term brand building. Positions open across design, retail, e-commerce, marketing, operations and creative direction.</p>
        </div>

        {/* Culture */}
        <section className="py-16 grid md:grid-cols-3 gap-12 mb-16" style={{ background: "#f4ecd8" }}>
          {[
            ["Creativity","Freedom to shape what LOOXS becomes."],
            ["Ownership","Small teams, real responsibility, real impact."],
            ["Craft","Uncompromising quality across every discipline."],
          ].map((c,i) => (
            <div key={i} className="px-8">
              <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-3">{String(i+1).padStart(2,"0")}</p>
              <h3 className="font-editorial text-2xl mb-3">{c[0]}</h3>
              <div className="rule-gold w-10 mb-3" />
              <p className="text-ink/70 text-sm leading-relaxed">{c[1]}</p>
            </div>
          ))}
        </section>

        {/* Positions */}
        <section className="mb-24">
          <h2 className="font-editorial text-3xl md:text-4xl mb-8">Open positions</h2>
          {jobs.length === 0 ? (
            <div className="border border-black/10 py-16 px-8 text-center" style={{ background: "#f9f4e8" }}>
              <p className="text-[10px] tracking-widest-plus uppercase text-goldDark mb-4">Status</p>
              <p className="font-editorial text-3xl">Currently no open positions.</p>
              <p className="mt-4 text-ink/60">Join our talent community below and we will reach out when the right role opens.</p>
              <p className="mt-6 text-sm text-ink/70">Direct applications: <a href="mailto:careers@looxsfashion.com" data-testid="careers-inline-email" className="link-draw text-goldDark">careers@looxsfashion.com</a></p>
            </div>
          ) : (
            <ul className="divide-y divide-black/10 border border-black/10">
              {jobs.map(j => (
                <li key={j.id} className="p-6 flex items-start justify-between gap-6">
                  <div>
                    <p className="text-[10px] tracking-widest-plus uppercase text-goldDark mb-1">{j.department}</p>
                    <p className="font-editorial text-2xl">{j.title}</p>
                    <p className="text-sm text-ink/60 mt-1">{j.location} · {j.employment_type}</p>
                  </div>
                  <a href={`mailto:careers@looxsfashion.com?subject=${encodeURIComponent("Application — " + j.title)}`} className="tracking-widest-plus text-[11px] uppercase link-draw whitespace-nowrap">Apply →</a>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Talent community */}
        <section className="max-w-2xl mx-auto">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6 text-center">Talent community</p>
          <h2 className="font-editorial text-3xl md:text-4xl text-center mb-10">Introduce yourself.</h2>
          <form onSubmit={submit} className="space-y-6">
            {[["name","Name","text"],["email","Email","email"],["phone","Phone","tel"]].map(([k,l,t]) => (
              <label key={k} className="block">
                <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">{l}</span>
                <input required data-testid={`careers-${k}`} type={t} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})}
                  className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent" />
              </label>
            ))}
            <label className="block">
              <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Area of interest</span>
              <select value={f.area} onChange={(e)=>setF({...f,area:e.target.value})} className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent text-ink">
                {["Design","Creative","Fashion","Retail","E-commerce","Marketing","Brand","Operations","Supply Chain","Technology","Customer Experience","Corporate"].map(x => <option key={x}>{x}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Message / portfolio</span>
              <textarea rows="4" data-testid="careers-cover" value={f.cover} onChange={(e)=>setF({...f,cover:e.target.value})}
                className="w-full border-b border-ink/25 focus:border-goldDark py-2 outline-none bg-transparent resize-none" />
            </label>
            <button data-testid="careers-submit" disabled={busy} className="w-full bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-goldDark transition-colors duration-500 disabled:opacity-40">
              {busy ? "Sending…" : "Join the talent community"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
